import { useState } from "react";
import { TbTrash, TbGripVertical, TbChevronDown } from "react-icons/tb";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import type { CreateStepForm } from "@/types/task";
import { useAllUsers } from "@/hooks/useUsers";
import { useTeams } from "@/hooks/useTeams";
import { useLookupValues } from "@/hooks/useTasks";
import { useAuth } from "@/hooks/useAuth";
import { ensureProtocol } from "@/lib/linkify";

interface NewTaskStepItemProps {
  step: Omit<CreateStepForm, "step_order" | "sequence"> & {
    tempId: string;
    // Override the boolean type to match form handling (strings)
    is_completed?: boolean | string;
  };
  isViewMode?: boolean;
  onUpdate: (tempId: string, field: string, value: string) => void;
  onRemove: (tempId: string) => void;
  hasError: boolean;
  errorMessage?: string;
  initialExpanded?: boolean;
}

export const NewTaskStepItem = ({
  step,
  onUpdate,
  onRemove,
  hasError,
  errorMessage,
  isViewMode = false,
  initialExpanded = false,
}: NewTaskStepItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: step.tempId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  // Get available users and teams for assignment using React Query
  const { data: users = [], isLoading: usersLoading } = useAllUsers();
  const { data: teams = [], isLoading: teamsLoading } = useTeams();
  const { data: step_statuses = [], isLoading: statusesLoading } =
    useLookupValues("step_status");
  const { user } = useAuth();
  const [isExpanded, setIsExpanded] = useState(initialExpanded);

  const assigneeTypeOptions = [
    { value: "", label: "Select" },
    { value: "User", label: "User" },
    { value: "Team", label: "Team" },
  ];

  const getAssigneeOptions = () => {
    if (!step.assignee_type) return [];

    if (step.assignee_type === "User") {
      return users.map((user) => ({
        value: user.id,
        label:
          user.first_name || user.last_name
            ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
            : user.email,
      }));
    }

    if (step.assignee_type === "Team") {
      return teams.map((team) => ({
        value: team.id,
        label: team.name,
      }));
    }

    return [];
  };

  const getStatusOptions = () => {
    return [
      { value: "", label: "Select Status" },
      ...step_statuses.map((status) => ({
        value: status.id,
        label: status.label,
      })),
    ];
  };

  const handleStatusChange = (statusId: string) => {
    onUpdate(step.tempId, "status_id", statusId);

    // Find the status to check if it's "completed"
    const selectedStatus = step_statuses.find((s) => s.id === statusId);
    const isCompleted = selectedStatus?.value === "completed";

    if (isCompleted) {
      // Set completion fields when status is completed
      onUpdate(step.tempId, "is_completed", "true"); // Convert to string for form handling
      onUpdate(step.tempId, "completed_at", new Date().toISOString());
      onUpdate(step.tempId, "completed_by", user?.id || "");
    } else {
      // Clear completion fields when status is not completed
      onUpdate(step.tempId, "is_completed", "false"); // Convert to string for form handling
      onUpdate(step.tempId, "completed_at", "");
      onUpdate(step.tempId, "completed_by", "");
    }
  };

  const formatCompletedDate = (dateString?: string) => {
    if (!dateString) return "";
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return "";
    }
  };

  const isStepCompleted = (completed?: boolean | string) => {
    return completed === true || completed === "true";
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-gray-100 border border-gray-100 rounded-2xl overflow-hidden"
    >
      {/* Step Header - Draggable */}
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            {!isViewMode && (
              <div
                className="cursor-grab active:cursor-grabbing"
                {...attributes}
                {...listeners}
              >
                <TbGripVertical
                  size={24}
                  className="text-[#828282] cursor-grab"
                />
              </div>
            )}
            <span className="text-[#444444] text-[14px] font-semibold">
              {step.step_name}
            </span>
          </div>

          <div className="flex items-center gap-5">
            {!isViewMode && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(step.tempId);
                }}
                className="text-[#828282] cursor-pointer hover:text-red-500"
              >
                <TbTrash size={24} />
              </button>
            )}
            <div className="w-0 h-[17px] border-l border-gray-300"></div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              className="text-[#828282] cursor-pointer"
            >
              <TbChevronDown
                size={20}
                className={`transition-transform ${
                  isExpanded ? "rotate-180" : "rotate-0"
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Step Form - Expanded */}
      {isExpanded && (
        <div className="bg-white">
          <div
            className="p-4 space-y-3"
            onKeyDown={(e) => {
              // Prevent any form submission when pressing Enter in this container
              if (e.key === "Enter" && e.target !== e.currentTarget) {
                e.stopPropagation();
              }
            }}
          >
            {/* Step Name */}
            <div className="space-y-1.5">
              <label className="text-[#444444] text-[14px] font-semibold">
                Step Name
              </label>
              <div className="bg-gray-100 rounded-[10px] px-3.5 py-[13px]">
                <input
                  type="text"
                  value={step.step_name || ""}
                  onChange={(e) =>
                    onUpdate(step.tempId, "step_name", e.target.value)
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                  className="w-full bg-transparent text-[#444444] text-[12px] placeholder:text-[#828282] border-none outline-none"
                  placeholder="Type Here"
                  disabled={isViewMode}
                />
              </div>
              {hasError && (
                <p className="text-red-500 text-xs">{errorMessage}</p>
              )}
            </div>

            {/* Step Description */}
            <div className="space-y-1.5">
              <label className="text-[#444444] text-[14px] font-semibold">
                Step Descriptions
              </label>
              <div className="bg-gray-100 rounded-[10px] px-3.5 py-[13px]">
                <textarea
                  value={step.description || ""}
                  onChange={(e) =>
                    onUpdate(step.tempId, "description", e.target.value)
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                  className="w-full bg-transparent text-[#444444] text-[12px] placeholder:text-[#828282] border-none outline-none resize-none"
                  placeholder="Type Here"
                  rows={2}
                  disabled={isViewMode}
                />
              </div>
            </div>

            {/* Assignment Type & Assignee */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-[#444444] text-[14px] font-semibold">
                  Assignment Type
                </label>
                <SearchableSelect
                  options={assigneeTypeOptions}
                  value={step.assignee_type || ""}
                  onValueChange={(value) => {
                    onUpdate(step.tempId, "assignee_type", value);
                    if (value !== step.assignee_type) {
                      onUpdate(step.tempId, "assignee", "");
                    }
                  }}
                  placeholder="Select"
                  className="bg-gray-100 rounded-[10px] px-3.5 py-[13px] text-[#828282] text-[12px]"
                  disabled={isViewMode}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[#444444] text-[14px] font-semibold">
                  Assignee
                </label>
                <SearchableSelect
                  options={getAssigneeOptions()}
                  value={step.assignee || ""}
                  onValueChange={(value) =>
                    onUpdate(step.tempId, "assignee", value)
                  }
                  placeholder={
                    step.assignee_type
                      ? "Select"
                      : "Select assignment type first"
                  }
                  disabled={
                    !step.assignee_type ||
                    usersLoading ||
                    teamsLoading ||
                    isViewMode
                  }
                  className="bg-gray-100 rounded-[10px] px-3.5 py-[13px] text-[#828282] text-[12px] disabled:opacity-50"
                />
              </div>
            </div>

            {/* Reference Link & Survey Link */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-[#444444] text-[14px] font-semibold">
                  Reference Link
                </label>
                {isViewMode && step.reference_link ? (
                  <a
                    href={ensureProtocol(step.reference_link)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block bg-gray-100 rounded-[10px] px-3.5 py-[13px] text-blue-600 hover:text-blue-800 text-[12px] underline break-all"
                  >
                    {step.reference_link}
                  </a>
                ) : (
                  <div className="bg-gray-100 rounded-[10px] px-3.5 py-[13px]">
                    <input
                      type="text"
                      value={step.reference_link || ""}
                      onChange={(e) =>
                        onUpdate(step.tempId, "reference_link", e.target.value)
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          e.stopPropagation();
                        }
                      }}
                      className="w-full bg-transparent text-[#444444] text-[12px] placeholder:text-[#828282] border-none outline-none"
                      placeholder="Type Here"
                      disabled={isViewMode}
                    />
                  </div>
                )}
              </div>

              <div className="space-y-1.5">
                <label className="text-[#444444] text-[14px] font-semibold">
                  Survey Link
                </label>
                {isViewMode && step.survey_link ? (
                  <a
                    href={ensureProtocol(step.survey_link)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block bg-gray-100 rounded-[10px] px-3.5 py-[13px] text-blue-600 hover:text-blue-800 text-[12px] underline break-all"
                  >
                    {step.survey_link}
                  </a>
                ) : (
                  <div className="bg-gray-100 rounded-[10px] px-3.5 py-[13px]">
                    <input
                      type="text"
                      value={step.survey_link || ""}
                      onChange={(e) =>
                        onUpdate(step.tempId, "survey_link", e.target.value)
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          e.stopPropagation();
                        }
                      }}
                      className="w-full bg-transparent text-[#444444] text-[12px] placeholder:text-[#828282] border-none outline-none"
                      placeholder="Type Here"
                      disabled={isViewMode}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Status & Completion */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-[#444444] text-[14px] font-semibold">
                  Status
                </label>
                <SearchableSelect
                  options={getStatusOptions()}
                  value={step.status_id || ""}
                  onValueChange={handleStatusChange}
                  placeholder="Select Status"
                  disabled={statusesLoading || isViewMode}
                  className="bg-gray-100 rounded-[10px] px-3.5 py-[13px] text-[#828282] text-[12px] disabled:opacity-50"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[#444444] text-[14px] font-semibold">
                  Completed
                </label>
                <div className="bg-gray-100 rounded-[10px] px-3.5 py-[13px] min-h-[45px] flex flex-col justify-center">
                  {isStepCompleted(step.is_completed) ? (
                    <div className="text-[#444444] text-[12px]">
                      <div className="font-medium text-green-600">
                        ✓ Completed
                      </div>
                      {step.completed_at && (
                        <div className="text-[10px] text-[#828282] mt-1">
                          {formatCompletedDate(step.completed_at)}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-[#828282] text-[12px]">
                      Not completed
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Save Button */}
            {/* <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setIsExpanded(!isExpanded);
              }}
              className="px-6 py-3 border border-[#374795] cursor-pointer rounded-[10px] text-[#374795] text-[14px] font-semibold hover:bg-blue-50 transition-colors"
            >
              Save
            </button> */}
          </div>
        </div>
      )}
    </div>
  );
};
